import { useState } from 'react';
import { Box, Skeleton,Typography, useTheme, Chip, Tabs, Tab, Button, Tooltip } from "@mui/material"
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AddIcon from '@mui/icons-material/Add';

import { Table, usePagination, Column } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { 
  useGetPaginatedClientUploads, 
  useGetPaginatedCaseUploads,
  DataUploadStatus, 
  ClientUploadRecord,
  CaseUploadRecord 
} from '@frontend/features/data-uploads/queries';
import { CreateClientUploadModal } from '@frontend/features/data-uploads/components/CreateClientUploadModal';
import { CreateCaseUploadModal } from '@frontend/features/data-uploads/components/CreateCaseUploadModal';

type UploadType = 'clients' | 'cases';

const TABLE_HEIGHT_WITH_AGENCIES_LAYOUT = 'calc(100vh - 300px)';

const FileNameLink = ({ filename, url }: { filename: string | null, url: string | null }) => {
  if (!filename || !url) return null;
  
  return (
    <Tooltip title={filename} placement="top">
      <Typography
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          color: 'primary.main',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          }
        }}
      >
        {filename}
      </Typography>
    </Tooltip>
  );
};

export const DataUploadsView = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const clientPagination = usePagination();
  const casePagination = usePagination();
  
  const [activeTab, setActiveTab] = useState<UploadType>('clients');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { data: clientData, isLoading: isLoadingClients } = useGetPaginatedClientUploads({
    agencyId: Number(id),
    page: clientPagination.page,
    limit: clientPagination.rowsPerPage,
  });

  const { data: caseData, isLoading: isLoadingCases } = useGetPaginatedCaseUploads({
    agencyId: Number(id),
    page: casePagination.page,
    limit: casePagination.rowsPerPage,
  });

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: UploadType) => {
    setActiveTab(newValue);
  };

  const clientColumns: Column<ClientUploadRecord>[] = [
    {
      id: 'status',
      label: t('dataUploads.clients.table.status'),
      minWidth: 150,
      align: 'left',
      format: (_, item: ClientUploadRecord) => (
        <Chip
          label={t(`dataUploads.clients.status.${item.status}`)}
          color={item.status === DataUploadStatus.COMPLETED ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'createdAt',
      label: t('dataUploads.clients.table.createdAt'),
      minWidth: 150,
      align: 'left',
      format: (_, item: ClientUploadRecord) => (
        <Typography variant="body2">
          {new Date(item.createdAt).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'createdBy',
      label: t('dataUploads.clients.table.createdBy'),
      minWidth: 200,
      align: 'left',
      format: (_, item: ClientUploadRecord) => (
        <Typography variant="body2">
          {`${item.createdByFirstName || ''} ${item.createdByLastName || ''}`}
        </Typography>
      ),
    },
    {
      id: 's3Filename',
      label: t('dataUploads.clients.table.filename'),
      minWidth: 150,
      align: 'left',
      format: (_, item: ClientUploadRecord) => (
        <FileNameLink filename={item.s3Filename} url={item.s3FileUrl} />
      ),
    },
  ];

  const caseColumns: Column<CaseUploadRecord>[] = [
    {
      id: 'status',
      label: t('dataUploads.cases.table.status'),
      minWidth: 150,
      align: 'left',
      format: (_, item: CaseUploadRecord) => (
        <Chip
          label={t(`dataUploads.cases.status.${item.status}`)}
          color={item.status === DataUploadStatus.COMPLETED ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'createdAt',
      label: t('dataUploads.cases.table.createdAt'),
      minWidth: 150,
      align: 'left',
      format: (_, item: CaseUploadRecord) => (
        <Typography variant="body2">
          {new Date(item.createdAt).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'createdBy',
      label: t('dataUploads.cases.table.createdBy'),
      minWidth: 200,
      align: 'left',
      format: (_, item: CaseUploadRecord) => (
        <Typography variant="body2">
          {`${item.createdByFirstName || ''} ${item.createdByLastName || ''}`}
        </Typography>
      ),
    },
    {
      id: 's3Filename',
      label: t('dataUploads.cases.table.filename'),
      minWidth: 150,
      align: 'left',
      format: (_, item: CaseUploadRecord) => (
        <FileNameLink filename={item.s3Filename} url={item.s3FileUrl} />
      ),
    },
  ];

  return (
    <ListLayout
      title={t('dataUploads.title')}
      subtitle={t('dataUploads.subtitle')}
      padding={0}
    >
      <Box sx={{ borderBottom: 1,gap: 1, borderColor: 'divider', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={t('dataUploads.tabs.clients')} value="clients" />
          <Tab label={t('dataUploads.tabs.cases')} value="cases" />
        </Tabs>
        
        <Button
          sx={{ width: '300px' }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenUploadModal}
          disableElevation
        >
          {t(activeTab === 'clients' ? 'dataUploads.clients.create.title' : 'dataUploads.cases.create.title')}
        </Button>
        
      </Box>

      {activeTab === 'clients' && (
        <>
          {clientData?.count !== undefined && clientData.count > 0 && (
            <Table
              height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
              columns={clientColumns}
              data={{
                key: 'id',
                items: clientData.items,
                totalCount: clientData.count,
              }}
              pagination={clientPagination}
              stickyHeader
            />
          )}
          {isLoadingClients && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
        </>
      )}

      {activeTab === 'cases' && (
        <>
          {caseData?.count !== undefined && caseData.count > 0 && (
            <Table
              height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
              columns={caseColumns}
              data={{
                key: 'id',
                items: caseData.items,
                totalCount: caseData.count,
              }}
              pagination={casePagination}
              stickyHeader
            />
          )}
          {isLoadingCases && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
        </>
      )}

      {activeTab === 'clients' ? (
        <CreateClientUploadModal
          open={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          agencyId={id || ''}
        />
      ) : (
        <CreateCaseUploadModal
          open={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          agencyId={id || ''}
        />
      )}
    </ListLayout>
  );
};
